import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  Loader2,
  Lock,
  MessageCircle,
  RefreshCw,
  Video,
} from 'lucide-react';
import { InterviewPublicAppBar } from './InterviewPublicAppBar';
import { isPlausiblePublicToken, fetchWithTimeout } from '@core/utils/publicToken';
import { getInterviewPublicEdgeUrl, interviewEdgeConfigErrorMessage } from '@core/utils/interviewEnv';
import { interviewEdgeUndeployedMessage, parseInterviewEdgeJson } from '@core/utils/interviewEdgeResponse';
import {
  AI_INTERVIEW_CONSENT_VERSION,
  AI_INTERVIEW_SESSION_TYPES,
  interviewTargetMinutesForSessionType,
  sessionInterviewStepLabel,
} from './interviewPrepConstants';

function publicInterviewHeaders(): Record<string, string> {
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  };
}

export function InterviewPrepPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isPopupMode = searchParams.get('popup') === '1';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [sessionType, setSessionType] = useState('screening');
  const [sessionStatus, setSessionStatus] = useState('');

  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [cameraId, setCameraId] = useState('');
  const [micId, setMicId] = useState('');
  const [speakerId, setSpeakerId] = useState('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [deviceErr, setDeviceErr] = useState('');
  const [prepSuccess, setPrepSuccess] = useState('');
  const [startBusy, setStartBusy] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [testSoundBusy, setTestSoundBusy] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const interviewEdgeUrl = getInterviewPublicEdgeUrl();

  const applyStream = useCallback((next: MediaStream) => {
    streamRef.current = next;
    setStream(next);
    if (videoRef.current) videoRef.current.srcObject = next;
  }, []);

  const syncDeviceIdsFromStream = useCallback(async () => {
    const list = await navigator.mediaDevices.enumerateDevices();
    const vIn = list.filter((d) => d.kind === 'videoinput');
    const aIn = list.filter((d) => d.kind === 'audioinput');
    const aOut = list.filter((d) => d.kind === 'audiooutput');
    setVideoInputs(vIn);
    setAudioInputs(aIn);
    setAudioOutputs(aOut);
    const cur = streamRef.current;
    if (!cur) return;
    const vt = cur.getVideoTracks()[0];
    const at = cur.getAudioTracks()[0];
    const vSettings = vt?.getSettings?.() as { deviceId?: string } | undefined;
    const aSettings = at?.getSettings?.() as { deviceId?: string } | undefined;
    if (vSettings?.deviceId) setCameraId(vSettings.deviceId);
    else if (vIn[0]?.deviceId) setCameraId(vIn[0].deviceId);
    if (aSettings?.deviceId) setMicId(aSettings.deviceId);
    else if (aIn[0]?.deviceId) setMicId(aIn[0].deviceId);
    if (aOut[0]?.deviceId) setSpeakerId(aOut[0].deviceId);
  }, []);

  /**
   * Prefer `ideal` over `exact` (exact fails if the id is stale). Fall back to default devices.
   */
  const replaceStream = useCallback(
    async (cam: string, mic: string) => {
      setDeviceErr('');
      setPrepSuccess('');
      streamRef.current?.getTracks().forEach((t) => t.stop());

      const tryGet = async (constraints: MediaStreamConstraints): Promise<MediaStream | null> => {
        try {
          return await navigator.mediaDevices.getUserMedia(constraints);
        } catch {
          return null;
        }
      };

      const hasCam = Boolean(cam?.trim());
      const hasMic = Boolean(mic?.trim());

      const attempts: MediaStreamConstraints[] = [];
      if (hasCam || hasMic) {
        attempts.push({
          video: hasCam ? { deviceId: { ideal: cam } } : true,
          audio: hasMic ? { deviceId: { ideal: mic } } : true,
        });
      }
      attempts.push({ video: true, audio: true });

      let next: MediaStream | null = null;
      for (const c of attempts) {
        next = await tryGet(c);
        if (next) break;
      }

      if (!next) {
        setDeviceErr(
          'Could not access camera or microphone. Close other apps using them, check browser permissions (address bar lock icon), then click Restart devices.',
        );
        return;
      }

      applyStream(next);
      await syncDeviceIdsFromStream();
    },
    [applyStream, syncDeviceIdsFromStream],
  );

  useEffect(() => {
    if (!token || !isPlausiblePublicToken(token)) {
      setLoading(false);
      setError('Invalid interview link.');
      return;
    }

    const cfgErr = interviewEdgeConfigErrorMessage();
    if (cfgErr) {
      setLoading(false);
      setError(cfgErr);
      return;
    }
    if (!interviewEdgeUrl) {
      setLoading(false);
      setError(interviewEdgeConfigErrorMessage() ?? 'Interview API URL is not configured.');
      return;
    }

    let cancelled = false;
    fetchWithTimeout(`${interviewEdgeUrl}?token=${encodeURIComponent(token)}&mode=info`, {
      headers: publicInterviewHeaders(),
      timeoutMs: 30_000,
    })
      .then(async (r) => {
        const text = await r.text();
        const parsed = parseInterviewEdgeJson(text);
        if (!parsed.ok) {
          if (cancelled) return;
          setError(
            import.meta.env.DEV
              ? interviewEdgeUndeployedMessage(r.status)
              : 'Interview service is unavailable. Ask your admin to deploy the AI interview function.',
          );
          setLoading(false);
          return;
        }
        const data = parsed.data as {
          error?: string;
          org_name?: string;
          job_title?: string | null;
          session_type?: string;
          status?: string;
        };
        if (cancelled) return;
        if (r.status === 410) {
          setError(data.error ?? 'This link has expired.');
          setLoading(false);
          return;
        }
        if (!r.ok || data.error) {
          setError(data.error ?? 'Could not load this session.');
          setLoading(false);
          return;
        }
        if (data.status === 'draft') {
          setError('This interview is not available yet.');
          setLoading(false);
          return;
        }
        setOrgName(data.org_name ?? '—');
        setJobTitle(data.job_title ?? null);
        setSessionType(data.session_type ?? 'screening');
        setSessionStatus(data.status ?? '');
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const aborted = e instanceof Error && e.name === 'AbortError';
          const msg = e instanceof Error ? e.message : String(e);
          setError(
            aborted
              ? 'Request timed out. Check your connection and try again.'
              : import.meta.env.DEV
                ? `Network error: ${msg}. Deploy: supabase functions deploy ggj_b2b_ai_interview_public`
                : 'Network error. Check your connection and try again.',
          );
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, interviewEdgeUrl]);

  useEffect(() => {
    if (loading || error) return;
    let cancelled = false;
    let current: MediaStream | null = null;

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          if (!cancelled) setDeviceErr('This browser does not support camera/microphone access.');
          return;
        }
        current = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) {
          current.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = current;
        setStream(current);
        if (videoRef.current) videoRef.current.srcObject = current;
        const list = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const vIn = list.filter((d) => d.kind === 'videoinput');
        const aIn = list.filter((d) => d.kind === 'audioinput');
        const aOut = list.filter((d) => d.kind === 'audiooutput');
        setVideoInputs(vIn);
        setAudioInputs(aIn);
        setAudioOutputs(aOut);
        const vt = current.getVideoTracks()[0];
        const at = current.getAudioTracks()[0];
        const vSettings = vt?.getSettings?.() as { deviceId?: string } | undefined;
        const aSettings = at?.getSettings?.() as { deviceId?: string } | undefined;
        if (vSettings?.deviceId) setCameraId(vSettings.deviceId);
        else if (vIn.find((d) => d.deviceId)?.deviceId) setCameraId(vIn.find((d) => d.deviceId)!.deviceId);
        if (aSettings?.deviceId) setMicId(aSettings.deviceId);
        else if (aIn.find((d) => d.deviceId)?.deviceId) setMicId(aIn.find((d) => d.deviceId)!.deviceId);
        if (aOut[0]?.deviceId) setSpeakerId(aOut[0].deviceId);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Permission denied';
          setDeviceErr(
            `Camera/microphone: ${msg}. Use Chrome or Edge, allow access when prompted, and ensure no other app is using the camera.`,
          );
        }
      }
    })();

    const onDeviceChange = () => {
      void (async () => {
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          setVideoInputs(list.filter((d) => d.kind === 'videoinput'));
          setAudioInputs(list.filter((d) => d.kind === 'audioinput'));
          setAudioOutputs(list.filter((d) => d.kind === 'audiooutput'));
        } catch {
          /* ignore */
        }
      })();
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);

    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      current?.getTracks().forEach((t) => t.stop());
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [loading, error]);

  /** If we have a stream and device lists but ids are still empty, pick first real deviceId so selects stay in sync. */
  useEffect(() => {
    if (!stream) return;
    if (!micId) {
      const id = audioInputs.find((d) => d.deviceId)?.deviceId;
      if (id) setMicId(id);
    }
  }, [stream, micId, audioInputs]);

  useEffect(() => {
    if (!stream) return;
    if (!cameraId) {
      const id = videoInputs.find((d) => d.deviceId)?.deviceId;
      if (id) setCameraId(id);
    }
  }, [stream, cameraId, videoInputs]);

  async function postBody(body: Record<string, unknown>) {
    const url = getInterviewPublicEdgeUrl();
    if (!url) {
      return { r: new Response(null, { status: 503 }), data: { error: interviewEdgeConfigErrorMessage() ?? 'Not configured' } };
    }
    const r = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { ...publicInterviewHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...body }),
      timeoutMs: 60_000,
    });
    const text = await r.text();
    const parsed = parseInterviewEdgeJson(text);
    const data = (parsed.ok ? parsed.data : { error: interviewEdgeUndeployedMessage(r.status) }) as {
      error?: string;
      ok?: boolean;
    };
    return { r, data };
  }

  async function onStart() {
    if (!token) return;
    setStartBusy(true);
    setStartErr('');
    try {
      const { r: cR, data: cD } = await postBody({ action: 'consent', consent_version: AI_INTERVIEW_CONSENT_VERSION });
      if (!cR.ok || cD.error) {
        setStartErr(cD.error ?? 'Could not save consent.');
        return;
      }
      const { r: pR, data: pD } = await postBody({ action: 'permissions' });
      if (!pR.ok || pD.error) {
        setStartErr(pD.error ?? 'Could not save device permission.');
        return;
      }
      window.location.assign(`/interview/configure/${token}${isPopupMode ? '?popup=1' : ''}`);
    } finally {
      setStartBusy(false);
    }
  }

  async function playTestSound() {
    setTestSoundBusy(true);
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      type CtxWithSink = AudioContext & { setSinkId?: (id: string) => Promise<void> };
      const ctxSink = ctx as CtxWithSink;
      if (speakerId && typeof ctxSink.setSinkId === 'function') {
        try {
          await ctxSink.setSinkId(speakerId);
        } catch {
          /* browser may not support output routing */
        }
      }
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 440;
      g.gain.value = 0.12;
      o.start();
      o.stop(ctx.currentTime + 0.25);
      await new Promise((r) => setTimeout(r, 320));
      await ctx.close();
    } catch (e) {
      console.warn('[InterviewPrep] test sound', e);
    } finally {
      setTestSoundBusy(false);
    }
  }

  /** Quick mic level check using the current stream (user gesture). */
  async function testMicLevel() {
    setDeviceErr('');
    setPrepSuccess('');
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const s = streamRef.current;
      if (!s?.getAudioTracks().length) {
        await replaceStream(cameraId, micId);
      }
      const audioTrack = streamRef.current?.getAudioTracks()[0];
      if (!audioTrack) {
        setDeviceErr('No microphone track. Click Restart devices.');
        return;
      }
      const ctx = new Ctx();
      if (ctx.state === 'suspended') await ctx.resume();
      const src = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let peak = 0;
      for (let i = 0; i < 15; i++) {
        analyser.getByteFrequencyData(data);
        peak = Math.max(peak, ...data);
        await new Promise((r) => setTimeout(r, 50));
      }
      src.disconnect();
      await ctx.close();
      if (peak < 2) {
        setDeviceErr('Microphone is very quiet — check input level or select another mic.');
      } else {
        setPrepSuccess('Mic check OK — we detected audio from your microphone.');
        setTimeout(() => setPrepSuccess(''), 4000);
      }
    } catch (e) {
      setDeviceErr(e instanceof Error ? e.message : 'Mic test failed.');
    }
  }

  const applicationTitle =
    jobTitle?.trim() || `${orgName} · AI interview application`;

  const stepLabel = sessionInterviewStepLabel(sessionType);
  const prepMinutes = interviewTargetMinutesForSessionType(sessionType);
  const roundBadge =
    AI_INTERVIEW_SESSION_TYPES.find((t) => t.value === sessionType)?.label?.toUpperCase() ?? 'INTERVIEW';

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-sky-400" aria-hidden />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading interview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-slate-50 px-4 text-center text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <p className="text-sm text-amber-800 dark:text-amber-200/90">{error}</p>
        <button
          type="button"
          className="text-sm text-brand-700 underline dark:text-sky-400"
          onClick={() => window.history.back()}
        >
          Go back
        </button>
      </div>
    );
  }

  if (sessionStatus === 'completed' || sessionStatus === 'cancelled') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-4 dark:bg-slate-950">
        <p className="text-slate-700 dark:text-slate-300">This interview is already closed.</p>
        <Link to={`/interview/${token}`} className="text-sm text-brand-700 underline dark:text-sky-400">
          View transcript
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <InterviewPublicAppBar
        title="Interview setup"
        subtitle={jobTitle?.trim() ? `${jobTitle} · ${orgName}` : orgName}
        icon={<Video className="h-5 w-5" aria-hidden />}
        showContactSupport
      />

      <div className="mx-auto grid w-full max-w-[1400px] flex-1 grid-cols-1 items-start gap-6 px-4 py-6 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)_minmax(260px,300px)] lg:gap-8">
        {startErr && (
          <div className="col-span-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/30 dark:text-amber-100/95">
            {startErr}
          </div>
        )}
        {/* Left — application progress */}
        <aside className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4" />
              Go back
            </button>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            {jobTitle ? (
              <span className="max-w-[140px] truncate text-slate-500 dark:text-slate-400" title="Listing shared by your recruiter">
                View listing
              </span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>

          <div>
            <h2 className="text-base font-semibold leading-snug text-slate-900 dark:text-slate-50">{applicationTitle}</h2>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">2 of 4 steps done</p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full w-1/2 rounded-full bg-[var(--vscode-primary,#2563eb)] dark:bg-sky-500" />
            </div>
          </div>

          <ol className="space-y-0 overflow-hidden rounded-lg border border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-900/50">
            <li className="flex items-start gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-sky-400" aria-hidden />
              <span className="text-slate-500 line-through dark:text-slate-500">Upload resume</span>
            </li>
            <li className="flex items-start gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-sky-400" aria-hidden />
              <span className="text-slate-500 line-through dark:text-slate-500">Work authorization</span>
            </li>
            <li className="flex items-start gap-2 border-b border-slate-100 border-l-4 border-l-blue-600 bg-blue-50/80 px-3 py-2.5 dark:border-slate-800 dark:bg-sky-950/40 dark:border-l-sky-500">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-blue-600 dark:border-sky-400" aria-hidden />
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">{stepLabel}</span>
                <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {roundBadge}
                </span>
              </div>
            </li>
            <li className="flex items-start gap-2 px-3 py-2.5 text-slate-400 dark:text-slate-500">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-slate-300 dark:border-slate-600" aria-hidden />
              <span>Follow-up</span>
            </li>
          </ol>
        </aside>

        {/* Middle — camera & devices */}
        <section className="flex min-w-0 flex-col gap-4">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">{stepLabel}</h2>
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                ~{prepMinutes} min
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              About {prepMinutes} minutes for this {stepLabel.toLowerCase()} — we&apos;ll keep it focused and respectful of
              your time.
            </p>
          </div>

          <div className="relative flex aspect-video max-h-[min(52vh,420px)] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-black dark:border-slate-700">
            <video ref={videoRef} className="h-full w-full object-cover" playsInline autoPlay muted />
            {!stream && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900 p-4 text-center text-sm text-slate-400">
                {deviceErr || 'Camera preview'}
              </div>
            )}
          </div>

          {deviceErr && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
              {deviceErr}
            </p>
          )}
          {prepSuccess && (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-100">
              {prepSuccess}
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Microphone</label>
              <select
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={micId || audioInputs.find((d) => d.deviceId)?.deviceId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setMicId(v);
                  void replaceStream(cameraId, v);
                }}
              >
                {audioInputs.length === 0 && <option value="">No device selected</option>}
                {audioInputs.map((d, i) => (
                  <option key={d.deviceId || `audio-in-${i}`} value={d.deviceId}>
                    {d.label || (d.deviceId ? `Microphone ${d.deviceId.slice(0, 8)}…` : `Microphone ${i + 1}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="mt-1 text-xs text-blue-700 hover:underline dark:text-sky-400"
                onClick={() => void testMicLevel()}
              >
                Test your mic
              </button>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Speakers</label>
              <select
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={speakerId}
                onChange={(e) => setSpeakerId(e.target.value)}
              >
                {audioOutputs.length === 0 && <option value="">Default</option>}
                {audioOutputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || 'Speakers'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={testSoundBusy}
                className="mt-1 text-xs text-blue-700 hover:underline disabled:opacity-50 dark:text-sky-400"
                onClick={() => void playTestSound()}
              >
                Play test sound
              </button>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Camera</label>
              <select
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={cameraId || videoInputs.find((d) => d.deviceId)?.deviceId || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setCameraId(v);
                  void replaceStream(v, micId);
                }}
              >
                {videoInputs.length === 0 && <option value="">No device selected</option>}
                {videoInputs.map((d, i) => (
                  <option key={d.deviceId || `video-in-${i}`} value={d.deviceId}>
                    {d.label || (d.deviceId ? `Camera ${d.deviceId.slice(0, 8)}…` : `Camera ${i + 1}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:underline dark:text-sky-400"
                onClick={() => void replaceStream(cameraId, micId)}
              >
                <RefreshCw className="h-3 w-3" />
                Restart devices
              </button>
            </div>
          </div>

          <button
            type="button"
            className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:border-slate-400 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 sm:w-auto"
            onClick={() => window.open('https://support.google.com/meet/answer/6370344', '_blank', 'noopener,noreferrer')}
          >
            Troubleshooting help
          </button>
        </section>

        {/* Right — things to know */}
        <aside className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">Things to know before starting</h3>
          <ul className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex gap-2">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>Expect about {prepMinutes} minutes for this round (length varies by interview type).</span>
            </li>
            <li className="flex gap-2">
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>Need assistance? Just ask in the chat.</span>
            </li>
            <li className="flex gap-2">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>Your recruiter may allow additional attempts if needed.</span>
            </li>
            <li className="flex gap-2">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>Your data is handled in line with your recruiter&apos;s privacy policy.</span>
            </li>
            <li className="flex gap-2">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
              <span>Complete this interview at your own pace.</span>
            </li>
          </ul>
          <button
            type="button"
            disabled={startBusy}
            onClick={() => void onStart()}
            className="btn-primary mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-800 py-3 text-sm font-semibold shadow-sm dark:border-slate-600"
          >
            {startBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <ChevronRight className="h-4 w-4" />}
            Start
          </button>
        </aside>
      </div>
    </div>
  );
}
